import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, MenuItem, Sidebar } from "react-pro-sidebar";
import { useContext, useEffect, useRef, useState } from "react";
import { Typography } from "../utilities/Typhograpy";
import "../../style/menu.css";
import GlobalContext from "../../context/GlobalContext";
import { BadgeComponent } from "../utilities/Badge";
import sewoorkLogo from "../../assets/images/sewoork-logo.png";
import SelectWorkspace from "../select/SelectWorkspace";
import { useTranslation } from "react-i18next";
import {
  GetListNotifications,
  GetRealtimeSocketTicket,
} from "../../api/NotificationApi";

// Lucide Icons
import {
  Home,
  Bell,
  Users,
  CalendarCheck,
  DollarSign,
  BarChart2,
  Settings,
} from "lucide-react";

const themes = {
  light: {
    // Warna mengikuti design system Sewoork (src/style/theme.css)
    sidebar: { backgroundColor: "#ffffff", color: "#3d4d6a" },
    menu: {
      menuContent: "#fbfcfd",
      icon: "#6b7a94",
      activeIcon: "#0d6ef0",
      hover: { backgroundColor: "#eef5ff", color: "#0d6ef0" }, // soft hover
      disabled: { color: "#9fb6cf" },
    },
  },
  dark: {
    sidebar: { backgroundColor: "#0b2948", color: "#8ba1b7" },
    menu: {
      menuContent: "#082440",
      icon: "#59d0ff",
      activeIcon: "#1e90ff",
      hover: { backgroundColor: "#123d66", color: "#d6e6f2" }, // soft hover dark
      disabled: { color: "#3e5e7e" },
    },
  },
};

// toggled/onClose: dipakai di layar < 992px, sidebar menjadi panel geser yang dibuka dari tombol menu di header.
const MenuComponent = ({ toggled = false, onClose }) => {
  const { user, updateActiveWorkspace } = useContext(GlobalContext);
  const { t } = useTranslation();

  // Pindah halaman dari menu sekaligus menutup panel sidebar di mobile/tablet.
  const go = (path) => {
    navigate(path);
    onClose?.();
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [theme, setTheme] = useState("light");
  const [unreadNotifications, setUnreadNotifications] = useState(
    user?.workspace?.unreads_notification || 0,
  );
  const previousUnreadRef = useRef(user?.workspace?.unreads_notification || 0);
  const baseTitleRef = useRef(document.title);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const hasBootstrappedRef = useRef(false);
  const desktopNotificationRef = useRef(null);

  const isNotificationRead = (status) => {
    const normalized = String(status ?? "").toLowerCase();

    return (
      normalized === "1" ||
      normalized === "read" ||
      normalized === "true" ||
      normalized === "yes"
    );
  };

  const updateTabTitle = (count) => {
    if (count > 0) {
      document.title = `${t("notifications.tab_title", { count })} - ${baseTitleRef.current}`;
      return;
    }

    document.title = baseTitleRef.current;
  };

  const showDesktopNotification = (unreadCount) => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      return;
    }

    if (Notification.permission !== "granted") {
      return;
    }

    if (!document.hidden && document.hasFocus()) {
      return;
    }

    if (desktopNotificationRef.current) {
      desktopNotificationRef.current.close();
    }

    const notification = new Notification(t("notifications.desktop_title"), {
      body: t("notifications.desktop_body", { count: unreadCount }),
      icon: `${window.location.origin}/favicon.ico`,
      tag: "console-comity-notification",
    });

    notification.onclick = () => {
      window.focus();
      navigate("/inbox");
      notification.close();
    };

    desktopNotificationRef.current = notification;

    setTimeout(() => {
      notification.close();
      if (desktopNotificationRef.current === notification) {
        desktopNotificationRef.current = null;
      }
    }, 8000);
  };

  useEffect(() => {
    if (!user?.user_id || typeof window === "undefined") {
      return;
    }

    if (!("Notification" in window) || Notification.permission !== "default") {
      return;
    }

    Notification.requestPermission().catch(() => {
      // Ignore browser-level permission failures.
    });
  }, [user?.user_id]);

  useEffect(() => {
    const currentUnread = user?.workspace?.unreads_notification || 0;
    setUnreadNotifications(currentUnread);
    previousUnreadRef.current = currentUnread;
    updateTabTitle(currentUnread);
  }, [user?.workspace?.unreads_notification]);

  useEffect(() => {
    if (!user?.user_id || !user?.workspace?.company_id) {
      return;
    }

    let isSubscribed = true;
    const handleUnreadUpdate = (unreadCount, options = {}) => {
      const { allowDesktopNotification = true } = options;

      if (
        unreadCount !== previousUnreadRef.current &&
        unreadCount > previousUnreadRef.current
      ) {
        updateTabTitle(unreadCount);

        if (allowDesktopNotification && hasBootstrappedRef.current) {
          showDesktopNotification(unreadCount);
        }
      } else if (unreadCount === 0) {
        updateTabTitle(0);
      }

      setUnreadNotifications(unreadCount);
      previousUnreadRef.current = unreadCount;

      if (unreadCount !== (user?.workspace?.unreads_notification || 0)) {
        updateActiveWorkspace({ unreads_notification: unreadCount });
      }
    };

    const syncUnreadNotificationsOnce = async () => {
      try {
        const result = await GetListNotifications({
          user_id: user.user_id,
          company_id: user.workspace.company_id,
        });

        if (!isSubscribed) {
          return;
        }

        const lists =
          result?.status === 200 && Array.isArray(result.data)
            ? result.data
            : [];

        const unreadCount = lists.filter(
          (item) => !isNotificationRead(item.notification_is_read),
        ).length;

        handleUnreadUpdate(unreadCount, { allowDesktopNotification: false });
      } catch (error) {
        // Ignore bootstrap errors; websocket updates will continue trying.
      } finally {
        if (isSubscribed) {
          hasBootstrappedRef.current = true;
        }
      }
    };

    const scheduleReconnect = () => {
      if (reconnectTimeoutRef.current || !isSubscribed) {
        return;
      }

      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        connectWebSocket();
      }, 5000);
    };

    const connectWebSocket = async () => {
      try {
        const ticketResult = await GetRealtimeSocketTicket({
          company_id: user.workspace.company_id,
        });

        if (!isSubscribed || ticketResult?.status !== 200) {
          scheduleReconnect();
          return;
        }

        const baseSocketUrl = ticketResult?.data?.socket_url;
        const ticket = ticketResult?.data?.ticket;

        if (!baseSocketUrl || !ticket) {
          scheduleReconnect();
          return;
        }

        const socketUrl = `${baseSocketUrl}${baseSocketUrl.includes("?") ? "&" : "?"}ticket=${encodeURIComponent(ticket)}`;
        wsRef.current = new WebSocket(socketUrl);

        wsRef.current.onopen = () => {
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
          }
        };

        wsRef.current.onmessage = (event) => {
          if (!isSubscribed) {
            return;
          }

          try {
            const payload = JSON.parse(event.data || "{}");
            const eventName = payload?.event || payload?.type;
            const nextCount = Number(
              payload?.unreads_notification ?? payload?.unread_count,
            );

            if (
              (eventName === "notification.unread_count" ||
                eventName === "notification.created" ||
                eventName === "notification.read") &&
              !Number.isNaN(nextCount)
            ) {
              handleUnreadUpdate(nextCount);
            }
          } catch (error) {
            // Ignore malformed websocket messages.
          }
        };

        wsRef.current.onerror = () => {
          if (wsRef.current) {
            wsRef.current.close();
          }
        };

        wsRef.current.onclose = () => {
          scheduleReconnect();
        };
      } catch (error) {
        scheduleReconnect();
      }
    };

    syncUnreadNotificationsOnce();
    connectWebSocket();

    return () => {
      isSubscribed = false;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      if (wsRef.current) {
        wsRef.current.close();
      }

      if (desktopNotificationRef.current) {
        desktopNotificationRef.current.close();
      }
    };
  }, [user?.user_id, user?.workspace?.company_id]);

  const getIconColor = (path) => {
    return location.pathname === path
      ? themes[theme].menu.activeIcon
      : themes[theme].menu.icon;
  };

  const getMenuItemStyle = (path) => ({
    borderRadius: "8px",
    margin: "2px 8px",
    padding: "2px 2px",
    fontSize: "14px",
    transition: "all 0.2s ease-in-out",
    backgroundColor:
      location.pathname === path
        ? themes[theme].menu.hover.backgroundColor
        : "",
    color:
      location.pathname === path ? themes[theme].menu.hover.color : "inherit",
    "&:hover": {
      backgroundColor: themes[theme].menu.hover.backgroundColor,
      color: themes[theme].menu.hover.color,
    },
  });

  return (
    <div style={{ display: "flex", height: "100%" }}>
      <Sidebar
        breakPoint="lg"
        toggled={toggled}
        onBackdropClick={onClose}
        backgroundColor={themes[theme].sidebar.backgroundColor}
        rootStyles={{ color: themes[theme].sidebar.color }}
      >
        <Menu>
          <div className="sidebar-brand">
            <img src={sewoorkLogo} alt="Sewoork" width="118" height="31" />
          </div>

          {/* Pemilih perusahaan: konteks kerja untuk semua halaman */}
          <div className="sidebar-workspace">
            <SelectWorkspace />
          </div>

          <div
            style={{
              padding: "0 24px",
              marginBottom: "8px",
              marginTop: "30px",
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              style={{ opacity: collapsed ? 0 : 0.7, letterSpacing: "0.5px" }}
            >
              {t("menu.title")}
            </Typography>
          </div>

          <MenuItem
            active={location.pathname === "/dashboard"}
            onClick={() => go("/dashboard")}
            icon={<Home size={20} color={getIconColor("/dashboard")} />}
            rootStyles={getMenuItemStyle("/dashboard")}
          >
            {t("menu.dashboard")}
          </MenuItem>

          <MenuItem
            active={location.pathname === "/inbox"}
            onClick={() => go("/inbox")}
            suffix={
              unreadNotifications > 0 ? (
                <BadgeComponent variant="info" shape="circle">
                  {unreadNotifications}
                </BadgeComponent>
              ) : null
            }
            icon={<Bell size={20} color={getIconColor("/inbox")} />}
            rootStyles={getMenuItemStyle("/inbox")}
          >
            {t("menu.inbox")}
          </MenuItem>

          <MenuItem
            active={location.pathname === "/users"}
            onClick={() => go("/users")}
            icon={<Users size={20} color={getIconColor("/users")} />}
            rootStyles={getMenuItemStyle("/users")}
          >
            {t("menu.users")}
          </MenuItem>

          <MenuItem
            active={location.pathname === "/attendance"}
            onClick={() => go("/attendance")}
            icon={
              <CalendarCheck size={20} color={getIconColor("/attendance")} />
            }
            rootStyles={getMenuItemStyle("/attendance")}
          >
            {t("menu.attendance")}
          </MenuItem>

          <MenuItem
            active={location.pathname === "/payroll"}
            onClick={() => go("/payroll")}
            icon={<DollarSign size={20} color={getIconColor("/payroll")} />}
            rootStyles={getMenuItemStyle("/payroll")}
          >
            {t("menu.payroll")}
          </MenuItem>

          <MenuItem
            active={location.pathname === "/analytics"}
            onClick={() => go("/analytics")}
            icon={<BarChart2 size={20} color={getIconColor("/analytics")} />}
            rootStyles={getMenuItemStyle("/analytics")}
          >
            {t("menu.analytics")}
          </MenuItem>

          <MenuItem
            active={location.pathname === "/settings"}
            onClick={() => go("/settings")}
            icon={<Settings size={20} color={getIconColor("/settings")} />}
            rootStyles={getMenuItemStyle("/settings")}
          >
            {t("menu.settings")}
          </MenuItem>
        </Menu>
      </Sidebar>
    </div>
  );
};

export default MenuComponent;
